'use client';

// useSuggestedQuestionsMutations - TanStack mutations for editing suggested questions
// Story 7.9: Creator Customizes Suggested Questions
//
// All mutations invalidate the read query key from useSuggestedQuestions so the
// editor and the read-only chips stay in sync without manual refetch().

import { useMutation, useQueryClient } from '@tanstack/react-query';

interface QuestionsResponse {
  data: { questions: string[] };
}

interface EnabledResponse {
  data: { enabled: boolean };
}

const queryKeys = {
  suggestions: (botId: string) => ['suggested-questions', botId] as const,
};

async function parseError(response: Response): Promise<Error> {
  const body = await response.json().catch(() => ({}));
  return new Error(body?.error?.message || 'Request failed');
}

/**
 * PUT — replace all 3 questions with creator-provided strings.
 */
export function useUpdateAllQuestions(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (questions: string[]): Promise<string[]> => {
      const response = await fetch(`/api/bots/${botId}/suggested-questions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions }),
      });
      if (!response.ok) throw await parseError(response);
      const result = (await response.json()) as QuestionsResponse;
      return result.data.questions;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suggestions(botId) });
    },
  });
}

/**
 * POST — regenerate the questions NOT in keepIndices.
 */
export function useRegenerateOne(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keepIndices: number[]): Promise<string[]> => {
      const response = await fetch(`/api/bots/${botId}/suggested-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keepIndices }),
      });
      if (!response.ok) throw await parseError(response);
      const result = (await response.json()) as QuestionsResponse;
      return result.data.questions;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suggestions(botId) });
    },
  });
}

/**
 * GET ?force=true — regenerate all 3 questions via AI.
 */
export function useRegenerateAll(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<string[]> => {
      const response = await fetch(
        `/api/bots/${botId}/suggested-questions?force=true`
      );
      if (!response.ok) throw await parseError(response);
      const result = (await response.json()) as QuestionsResponse;
      return result.data.questions;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suggestions(botId) });
    },
  });
}

/**
 * PATCH — toggle whether suggested questions are shown to consumers.
 */
export function useSetSuggestedQuestionsEnabled(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (enabled: boolean): Promise<boolean> => {
      const response = await fetch(`/api/bots/${botId}/suggested-questions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!response.ok) throw await parseError(response);
      const result = (await response.json()) as EnabledResponse;
      return result.data.enabled;
    },
    onSuccess: () => {
      // Invalidate both the bot record (source of truth for the enabled flag)
      // and the suggested-questions read query (per the Story 7.9 invalidation
      // contract — every successful mutation refreshes the reader).
      queryClient.invalidateQueries({ queryKey: ['bot', botId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.suggestions(botId) });
    },
  });
}
